import styled from "@emotion/styled"
import type { MarketplaceKey } from "@porsche-customer/slfinrtl-context-types"
import { currentEnvironment } from "@porsche-customer/slfinrtl-environment"
import { PButton, PSpinner } from "@porsche-design-system/components-react"

import { spacingFluidSm } from "@porsche-design-system/components-react/emotion"
// eslint-disable-next-line no-restricted-imports
import { getMarketplaceConfig } from "@slfinrtl/feature-config"
import { hasMarketExportAccess } from "@slfinrtl/ppn-roles"
import type {
	DealershipListItem,
	DealershipListOptions,
	DealershipListView,
	MarketExportView,
} from "@slfinrtl/ui-bff-routes"
import { getDealershipListOptionsRoute, getMarketExportViewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import useSWRInfinite from "swr/infinite"
import { getFeatureToggle } from "src/common/getConfig"
import { getJwtPayload } from "src/common/jwt/jwtUtils"
import { ContainerHeader, ContentWrapper, Main, Stage, StageList, Tooltip } from "src/components"
import { useModal } from "src/context/modal/useModal"
import { useInfiniteScroll } from "src/hooks"
import { getAxiosInstance } from "./common/getAxiosInstance/getAxiosInstance"
import { getRedirectLanguage } from "./common/getRedirectLanguage/getRedirectLanguage"
import { marketExportMessages } from "./common/marketExport.messages"
import type { Filters } from "./components/dealershipFilters/DealershipFilters"
import { DealershipFilters } from "./components/dealershipFilters/DealershipFilters"
import type { Sort } from "./components/dealershipList/DealershipList"
import { DealershipList } from "./components/dealershipList/DealershipList"
import { DealershipListSkeleton } from "./components/dealershipList/DealershipListSkeleton"
import { useDealershipList } from "./hooks/useDealershipList/useDealershipList"
import { useMarketExport } from "./hooks/useMarketExport/useMarketExport"
import { MarketExportModalContainer } from "./modals/MarketExportModalContainer"
import type { MarketExportModalContainerProps } from "./modals/MarketExportModalContainer"
import { RequestContext } from "./requestContext/RequestContext"

const messages = defineMessages({
	marketExportNoSelectionTooltip: {
		defaultMessage: "Please select a marketplace to export.",
		id: "dealerList.marketExportNoSelectionTooltip",
	},
	marketExportNotEnabledInMarketTooltip: {
		defaultMessage: "Vehicle export is not available in {marketplace}.",
		id: "dealerList.marketExportNotEnabledInMarketTooltip",
	},
	selectDealer: {
		defaultMessage: "Select a dealership",
		id: "dealerList.pageHeader",
	},
})

const PAGE_SIZE = 30
const DEFAULT_SORT: Sort = {
	field: "organisationFunction",
	sortDirection: "desc",
}

const getDefaultDealerUrl = (entryDn: string, language: string) =>
	`${window.location.origin}/dealer/${entryDn}/${language}`

const areFiltersEmpty = (filter: Filters | undefined): boolean =>
	!filter || (!filter.searchText && !filter.marketplace && !filter.organisationFunctions)

interface DealershipListScreenProps {
	getDealerAppUrl?: (dealershipId: string, locale: string) => string
}

const currentEnv = currentEnvironment()
const env = currentEnv === "local" || !currentEnv ? "dev" : currentEnv
const isMarketExportEnabledInMarket = (marketplaceKey: MarketplaceKey) => {
	const { marketDeliveredExportEnabled, marketInventoryExportEnabled } =
		getMarketplaceConfig(marketplaceKey, env) ?? {}
	return !!marketInventoryExportEnabled || !!marketDeliveredExportEnabled
}

export const DealershipListScreen = ({ getDealerAppUrl, ...rest }: DealershipListScreenProps) => {
	const { formatMessage } = useIntl()
	const getUrl = getDealerAppUrl ?? getDefaultDealerUrl
	const context = useContext(RequestContext)

	const [filter, setFilter] = useState<Filters | undefined>()
	const [sort, setSort] = useState<Sort>(DEFAULT_SORT)
	const [items, setItems] = useState<DealershipListView["result"]["items"]>([])
	const [view, setView] = useState<DealershipListView | undefined>()
	const [filterOptions, setFilterOptions] = useState<DealershipListOptions>()
	const [marketExportView, setMarketExportView] = useState<MarketExportView | null>()
	const [isMarketExportViewLoading, setIsMarketExportViewLoading] = useState<boolean>(false)

	const showMarketExportModal = useModal<MarketExportModalContainerProps>(MarketExportModalContainer)

	const fetchDealerships = useDealershipList()

	const jwtClaims = useMemo(() => getJwtPayload(), [])
	const isMarketExportFeatureEnabled = getFeatureToggle("market-export-enabled")
	const hasUserMarketExportAccess = !!jwtClaims && hasMarketExportAccess(jwtClaims)
	const hasUserAnyMarketExportEnabledMarketplace = filterOptions?.filter.marketplaces.options.some((marketplace) =>
		isMarketExportEnabledInMarket(marketplace.value),
	)
	const showMarketExport =
		isMarketExportFeatureEnabled && hasUserMarketExportAccess && hasUserAnyMarketExportEnabledMarketplace

	const fetchOptions = useCallback(async () => {
		const axiosInstance = await getAxiosInstance(context)
		const url = getDealershipListOptionsRoute.buildUrl({})
		const response = await axiosInstance[getDealershipListOptionsRoute.method](url, {
			params: { locale: navigator.language },
		})

		return response.data
	}, [context])

	const marketplaceKey = filter?.marketplace
	const fetchMarketExportView = useCallback(async () => {
		if (!marketplaceKey || !isMarketExportEnabledInMarket(marketplaceKey)) {
			return
		}

		const axiosInstance = await getAxiosInstance(context)
		const url = getMarketExportViewRoute.buildUrl({ marketplaceKey })
		setIsMarketExportViewLoading(true)
		const response = await axiosInstance[getMarketExportViewRoute.method](url, {
			params: { locale: navigator.language },
		})

		setMarketExportView(response.data)
		setIsMarketExportViewLoading(false)
	}, [context, marketplaceKey])

	useEffect(() => {
		async function loadMarketExportView() {
			await fetchMarketExportView()
		}

		void loadMarketExportView()
	}, [fetchMarketExportView])

	const exportMarketExcel = useMarketExport(marketplaceKey)

	const getKey = (pageIndex: number) => [
		{
			body: {
				filter,
				paging: {
					limit: PAGE_SIZE,
					offset: pageIndex * PAGE_SIZE,
				},
				sort,
			},
			pathParams: {},
			queryParams: { locale: navigator.language },
		},
	]
	const { data, isValidating, setSize } = useSWRInfinite(getKey, fetchDealerships, { revalidateAll: true })

	useEffect(() => {
		async function loadFilterOptions() {
			const options = await fetchOptions()
			setFilterOptions(options)
		}

		void loadFilterOptions()
	}, [fetchOptions])

	useEffect(() => {
		function updateViewAndItems() {
			const currentPageData = data ? data[data.length - 1] : undefined
			if (!currentPageData) {
				return
			}

			setView(currentPageData)
			setItems(
				currentPageData.paging.offset > 0
					? (previousItems) => previousItems.concat(currentPageData.result.items)
					: currentPageData.result.items,
			)
		}

		void updateViewAndItems()
	}, [data])

	const getDealerUrl = useCallback(
		(item: DealershipListItem, locale?: string) =>
			getUrl(
				item.entryDn,
				locale ?? getRedirectLanguage(item.languageTags, filterOptions?.user.preferredLanguage),
			),
		[getUrl, filterOptions?.user.preferredLanguage],
	)

	useEffect(() => {
		if (areFiltersEmpty(filter) && items.length === 1) {
			window.location.assign(getDealerUrl(items[0]))
		}
	}, [filter, getDealerUrl, items])

	useInfiniteScroll(() => {
		if (isValidating || !hasMore) {
			return
		}
		setSize((prev) => prev + 1)
	}, 1200)

	const hasMore = view?.result.hasMore
	const selectedMarketplaceName = marketplaceKey
		? (filterOptions?.filter.marketplaces.options.find((m) => m.value === marketplaceKey)?.label ?? marketplaceKey)
		: undefined
	return (
		<Main {...rest}>
			<ContentWrapper>
				<ContainerHeader title={formatMessage(messages.selectDealer)} />
			</ContentWrapper>

			<StageList>
				{filterOptions && (
					<Stage>
						<FilterActionsGrid>
							<DealershipFilters filterOptions={filterOptions} updateFilter={setFilter} />
							{showMarketExport && (
								<Tooltip
									content={
										!filter?.marketplace
											? formatMessage(messages.marketExportNoSelectionTooltip)
											: !isMarketExportEnabledInMarket(filter.marketplace) ||
													(marketExportView === null && !isMarketExportViewLoading)
												? formatMessage(messages.marketExportNotEnabledInMarketTooltip, {
														marketplace: selectedMarketplaceName,
													})
												: undefined
									}
								>
									<PButton
										type="button"
										variant="secondary"
										icon="download"
										loading={isMarketExportViewLoading}
										disabled={
											!filter?.marketplace ||
											!isMarketExportEnabledInMarket(filter.marketplace) ||
											!marketExportView
										}
										onClick={() => {
											if (!marketplaceKey || !marketExportView) {
												return
											}
											showMarketExportModal({
												marketplaceKey,
												onExport: exportMarketExcel,
												view: marketExportView,
											})
										}}
									>
										<FormattedMessage {...marketExportMessages.marketExport} />
									</PButton>
								</Tooltip>
							)}
						</FilterActionsGrid>
					</Stage>
				)}
				<Stage>
					{items && view ? (
						<DealershipList
							items={items}
							totalCount={view.result.totalCount}
							updateSort={setSort}
							getDealerAppUrl={getDealerUrl}
						/>
					) : (
						<DealershipListSkeleton />
					)}
					{isValidating && (
						<SpinnerWrapper>
							<PSpinner />
						</SpinnerWrapper>
					)}
				</Stage>
			</StageList>
		</Main>
	)
}

const SpinnerWrapper = styled.div`
	display: flex;
	justify-content: center;
`

const FilterActionsGrid = styled.div`
	display: grid;
	gap: ${spacingFluidSm};
	grid-template-columns: 1fr auto;
	align-items: flex-end;
`
